import React, { useRef, useState, useEffect, type UIEvent } from "react";

import { Icon } from "@local-logic/core/icons";

import { useNeighborhoodMap } from "~/sdks/NeighborhoodMap/context";
import { useRootElement } from "../context";

import * as styles from "./styles";

const arrowActivationDistance = 50;
const scrollOffset = 128;

export const ScrollList = ({ children }: { children?: React.ReactNode[] }) => {
  const { activePoiCategory } = useNeighborhoodMap();
  const { isFiltersDialogOpen } = useRootElement();
  const [lastActivePoiCategory, setLastActivePoiCategory] =
    useState(activePoiCategory);
  const [wasFiltersDialogOpen, setWasFiltersDialogOpen] =
    useState(isFiltersDialogOpen);
  const [isLeftArrowVisibile, setIsLeftArrowVisible] = useState(false);
  const [isRightArrowVisible, setIsRightArrowVisible] = useState(true);
  const scrollContainerRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    setWasFiltersDialogOpen(isFiltersDialogOpen);
  }, [isFiltersDialogOpen]);

  useEffect(() => {
    setLastActivePoiCategory(activePoiCategory);
  }, [activePoiCategory]);

  // This gets triggered when selecting an active POI from the popup. It's possible
  // to have an active POI that is not within the view which gets confusing
  useEffect(() => {
    // Don't need to scroll if the dialog is currently open
    if (isFiltersDialogOpen) return;

    // Don't need to scroll if the category hasn't changed
    if (activePoiCategory === lastActivePoiCategory) return;

    // Lastly, don't need to scroll if we're missing data or we didn't just close the dialog
    if (
      !scrollContainerRef.current ||
      !activePoiCategory ||
      !wasFiltersDialogOpen
    )
      return;

    // Find the active list element
    const activeItem = scrollContainerRef.current.querySelector(
      "li[data-active='true']",
    ) as HTMLLIElement;

    if (!activeItem) {
      return;
    }

    // Only scroll if it's not in view at all. It's not perfect but it works alright
    scrollContainerRef.current?.scroll({
      left: activeItem.offsetLeft - scrollOffset,
      behavior: "smooth",
    });
  }, [
    activePoiCategory,
    lastActivePoiCategory,
    isFiltersDialogOpen,
    wasFiltersDialogOpen,
  ]);

  const handleScrollLeftClick = () => {
    scrollContainerRef.current?.scroll({
      left:
        scrollContainerRef.current.scrollLeft -
        scrollContainerRef.current.clientWidth +
        scrollOffset,
      behavior: "smooth",
    });
  };

  const handleScrollRightClick = () => {
    scrollContainerRef.current?.scroll({
      left:
        scrollContainerRef.current.scrollLeft +
        scrollContainerRef.current.clientWidth -
        scrollOffset,
      behavior: "smooth",
    });
  };

  /**
   * Handles the scrolling left and right with a trackpad
   */
  const handleScrollEvent = (event: UIEvent<HTMLUListElement>) => {
    // |-Hidden-|-----Visible----->
    const leftScrollTriggerPoint = arrowActivationDistance;

    // <-----Visible-----|-Hidden-|
    const rightScrollTriggerPoint =
      event.currentTarget.scrollWidth -
      event.currentTarget.offsetWidth -
      arrowActivationDistance;

    // Left arrow
    if (
      event.currentTarget.scrollLeft > leftScrollTriggerPoint &&
      !isLeftArrowVisibile
    ) {
      setIsLeftArrowVisible(true);
    } else if (
      event.currentTarget.scrollLeft < leftScrollTriggerPoint &&
      isLeftArrowVisibile
    ) {
      setIsLeftArrowVisible(false);
    }

    // Right arrow
    if (
      event.currentTarget.scrollLeft < rightScrollTriggerPoint &&
      !isRightArrowVisible
    ) {
      setIsRightArrowVisible(true);
    } else if (
      event.currentTarget.scrollLeft > rightScrollTriggerPoint &&
      isRightArrowVisible
    ) {
      setIsRightArrowVisible(false);
    }
  };

  return (
    <div className={styles.container}>
      <ul
        className={styles.list}
        ref={scrollContainerRef}
        onScroll={handleScrollEvent}
      >
        {isLeftArrowVisibile && (
          <div
            className={styles.leftArrowContainer}
            onClick={handleScrollLeftClick}
          >
            <Icon icon="AngleArrowSmallLeft" className={styles.arrowIcon} />
          </div>
        )}

        {children}

        {isRightArrowVisible && (
          <div
            className={styles.rightArrowContainer}
            onClick={handleScrollRightClick}
          >
            <Icon icon="AngleArrowSmallRight" className={styles.arrowIcon} />
          </div>
        )}
      </ul>
    </div>
  );
};
